// ============================================
// Direct Messaging System - React Hooks
// ============================================

import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useSession } from '@/context/SessionContext';

const CHAT_SYNC_EVENT = 'chat:sync';

const emitChatSync = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CHAT_SYNC_EVENT));
  }
};

const isTransientChatFetchError = (error: any): boolean => {
  const message = String(error?.message || '').toLowerCase();
  const details = String(error?.details || '').toLowerCase();

  return message.includes('load failed')
    || message.includes('failed to fetch')
    || message.includes('networkerror')
    || message.includes('access control checks')
    || details.includes('load failed');
};

const isExpectedAnonSessionsError = (error: any): boolean => {
  const code = String(error?.code || '').toLowerCase();
  const message = String(error?.message || '').toLowerCase();
  return code === '42501' || message.includes('permission denied');
};

const getAnonymousSessionIdFromGroupDescription = (description?: string | null): string | null => {
  if (!description) return null;
  const match = String(description).trim().match(/^anonymous-support:([0-9a-fA-F-]{36})$/);
  return match?.[1] || null;
};

// Sonido de notificación Tritone
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const playNote = (freq: number, startTime: number, duration: number) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, startTime);
      gain.gain.setValueAtTime(0.1, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };
    const now = audioContext.currentTime;
    playNote(440, now, 0.1); // La
    playNote(554.37, now + 0.1, 0.1); // Do#
    playNote(659.25, now + 0.2, 0.1); // Mi
  } catch (e) {
    console.debug('No se pudo reproducir sonido de notificación:', e);
  }
};

import {
  getUserDMConversations,
  getUserGroupChats,
  getDMMessages,
  getGroupMessages,
  sendDMMessage,
  sendGroupMessage,
  markMessagesAsRead,
  updateUserPresence,
  getTypingUsersInDM,
  getTypingUsersInGroup,
  getUserPresence,
  getUsersPresence,
  isUserPresenceActive,
  getAnonymousSupportSessions,
  getAnonymousMessages,
  getAnonymousGroupMessages,
  sendAnonymousChatReply,
  saveAnonymousMessage,
  sendAnonymousUserGroupMessage,
} from '@/lib/supabase/chat';
import {
  clearAnonymousSupportConversationOnly,
  getDefaultSupportStoreId,
  listAnonymousSupportConversations,
  setAnonymousSupportConversation,
} from '@/lib/anonymousSupportChat';
import type {
  DMConversationWithDetails,
  GroupChatWithDetails,
  MessageWithSender,
  SendMessageParams,
  TypingUser,
  UserPresence,
} from '@/types/chat';
import { RealtimeChannel } from '@supabase/supabase-js';

// ============================================
// useChatConversations Hook
// ============================================
export const useChatConversations = () => {
  const [dmConversations, setDmConversations] = useState<DMConversationWithDetails[]>([]);
  const [groupChats, setGroupChats] = useState<GroupChatWithDetails[]>([]);
  const [anonymousSessions, setAnonymousSessions] = useState<any[]>([]);
  const anonymousUnreadRef = useRef<Record<string, number>>({});
  const groupUnreadRef = useRef<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();
  const { session } = useSession();

  const loadConversations = useCallback(async (options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent);
    try {
      if (!silent) {
        setLoading(true);
      }
      
      // Try DMs first
      let dms: DMConversationWithDetails[] = [];
      try {
        dms = await getUserDMConversations();
      } catch (dmError: any) {
        if (isTransientChatFetchError(dmError)) {
          console.debug('[useChatConversations] DMs temporalmente no disponibles (red/CORS).');
        } else {
          console.error('[useChatConversations] Error cargando DMs:', dmError);
        }
      }
      
      // Try groups
      let groups: GroupChatWithDetails[] = [];
      try {
        groups = await getUserGroupChats();
      } catch (groupError: any) {
        if (isTransientChatFetchError(groupError)) {
          console.debug('[useChatConversations] Grupos temporalmente no disponibles (red/CORS).');
        } else {
          console.error('[useChatConversations] Error cargando grupos:', groupError);
        }
      }

      const normalizedGroups = (groups || []).map((group) => {
        const previousUnread = group?.id ? groupUnreadRef.current[group.id] : undefined;
        const rpcUnread = Number(group?.unread_count || 0);
        const unreadCount = typeof previousUnread === 'number'
          ? Math.max(previousUnread, rpcUnread)
          : rpcUnread;

        return {
          ...group,
          unread_count: unreadCount,
        };
      });

      const nextGroupUnreadMap: Record<string, number> = {};
      normalizedGroups.forEach((group) => {
        if (!group?.id) return;
        nextGroupUnreadMap[group.id] = Number(group?.unread_count || 0);
      });
      groupUnreadRef.current = nextGroupUnreadMap;

      // Derive unread counters for anonymous sessions from their backing anonymous groups.
      const anonymousUnreadFromGroups: Record<string, number> = {};
      normalizedGroups.forEach((group) => {
        const sessionId = getAnonymousSessionIdFromGroupDescription((group as any)?.description);
        if (!sessionId) return;
        anonymousUnreadFromGroups[sessionId] = Number(group?.unread_count || 0);
      });

      // Try anonymous sessions (for staff)
      let anons: any[] = [];
      try {
        anons = await getAnonymousSupportSessions();
      } catch (anonError: any) {
        if (isExpectedAnonSessionsError(anonError) || isTransientChatFetchError(anonError)) {
          console.debug('[useChatConversations] Sesiones anónimas no disponibles (esperado).');
        } else {
          console.debug('[useChatConversations] Error cargando sesiones anónimas:', anonError);
        }
      }

      const normalizedAnons = (anons || []).map((session: any) => {
        const sessionId = session?.anonymous_session_id;
        const rpcUnread = typeof session?.unread_count === 'number' ? session.unread_count : null;
        const previousUnread = sessionId ? anonymousUnreadRef.current[sessionId] : undefined;
        const groupDerivedUnread = sessionId ? anonymousUnreadFromGroups[sessionId] : undefined;
        const fallbackUnread = session?.has_unread ? 1 : 0;

        const mergedUnreadCandidates = [
          typeof rpcUnread === 'number' ? rpcUnread : null,
          typeof previousUnread === 'number' ? previousUnread : null,
          typeof groupDerivedUnread === 'number' ? groupDerivedUnread : null,
          fallbackUnread,
        ].filter((value): value is number => typeof value === 'number');

        const unreadCount = mergedUnreadCandidates.length > 0
          ? Math.max(...mergedUnreadCandidates)
          : 0;

        return {
          ...session,
          unread_count: unreadCount,
          has_unread: unreadCount > 0,
        };
      });

      const nextUnreadMap: Record<string, number> = {};
      normalizedAnons.forEach((session: any) => {
        const sessionId = session?.anonymous_session_id;
        if (!sessionId) return;
        nextUnreadMap[sessionId] = Number(session?.unread_count || 0);
      });
      anonymousUnreadRef.current = nextUnreadMap;
      
      setDmConversations(dms);
      setGroupChats(normalizedGroups);
      setAnonymousSessions(normalizedAnons);
      setError(null);
      
    } catch (err: any) {
      console.error('[useChatConversations] Error general:', err);
      setError(err as Error);
      if (!silent) {
        toast({
          title: 'Error al cargar conversaciones',
          description: `${err.message || 'Error desconocido'}\nCódigo: ${err.code || 'N/A'}\nDetalles: ${err.details || 'N/A'}`,
          variant: 'destructive',
        });
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [toast]);

  useEffect(() => {
    loadConversations();

    const queueReloadRef = { timer: null as ReturnType<typeof setTimeout> | null };
    const queueReload = (delay = 120) => {
      if (queueReloadRef.timer) {
        clearTimeout(queueReloadRef.timer);
      }
      queueReloadRef.timer = setTimeout(() => {
        queueReloadRef.timer = null;
        console.debug('[useChatConversations] Reloading conversations...');
        loadConversations({ silent: true });
      }, delay);
    };

    // Subscribe to realtime updates for conversations
    const dmChannel = supabase
      .channel(`dm_conversations_changes_${Date.now()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'dm_conversations' }, () => {
        queueReload();
      })
      .subscribe();

    const groupChannel = supabase
      .channel(`group_chats_changes_${Date.now()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'group_chats' }, () => {
        queueReload();
      })
      .subscribe();

    // Subscribe to messages table to update conversations in real-time - AGGRESSIVE
    const messagesChannel = supabase
      .channel(`messages_updates_global_${Date.now()}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) => {
        const row = payload?.new as any;
        const metadata = typeof row?.metadata === 'string'
          ? (() => {
              try {
                return JSON.parse(row.metadata);
              } catch {
                return {};
              }
            })()
          : (row?.metadata || {});
        const groupId = row?.group_chat_id || row?.group_id;
        const senderId = row?.sender_id || null;
        const currentUserId = session?.user?.id || null;
        const anonymousSessionId = metadata?.anonymous_session_id as string | undefined;
        const isAnonymousOrigin = metadata?.is_anonymous === true
          || metadata?.is_anonymous === 'true'
          || Boolean(anonymousSessionId);
        const isIncomingForCurrentUser = isAnonymousOrigin || (senderId && senderId !== currentUserId);

        if (groupId && isIncomingForCurrentUser) {
          setGroupChats((prev) => {
            const idx = prev.findIndex((g) => g.id === groupId);
            if (idx === -1) return prev;

            const next = [...prev];
            const target = next[idx];
            const currentUnread = Number(target?.unread_count || 0);
            const nextUnread = currentUnread + 1;

            next[idx] = {
              ...target,
              last_message_at: row?.created_at || target?.last_message_at,
              unread_count: nextUnread,
            };

            groupUnreadRef.current[groupId] = nextUnread;
            return next;
          });
        }

        if (anonymousSessionId && isIncomingForCurrentUser) {
          setAnonymousSessions((prev) => {
            const idx = prev.findIndex((session: any) => session?.anonymous_session_id === anonymousSessionId);

            if (idx === -1) {
              const created = {
                anonymous_session_id: anonymousSessionId,
                support_store_id: null,
                last_message: row?.content || '',
                last_message_at: row?.created_at || new Date().toISOString(),
                message_count: 1,
                has_unread: true,
                unread_count: 1,
              };
              anonymousUnreadRef.current[anonymousSessionId] = 1;
              return [created, ...prev];
            }

            const next = [...prev];
            const current = next[idx] || {};
            const currentUnread = typeof current?.unread_count === 'number'
              ? current.unread_count
              : (current?.has_unread ? 1 : 0);
            const nextUnread = currentUnread + 1;

            next[idx] = {
              ...current,
              last_message: row?.content || current?.last_message,
              last_message_at: row?.created_at || current?.last_message_at,
              message_count: Number(current?.message_count || 0) + 1,
              has_unread: nextUnread > 0,
              unread_count: nextUnread,
            };

            next.sort((a: any, b: any) => {
              const aTs = a?.last_message_at ? new Date(a.last_message_at).getTime() : 0;
              const bTs = b?.last_message_at ? new Date(b.last_message_at).getTime() : 0;
              return bTs - aTs;
            });

            anonymousUnreadRef.current[anonymousSessionId] = nextUnread;
            return next;
          });
        }

        console.debug('[useChatConversations] Nuevo mensaje detectado, recargando conversations');
        queueReload(50); // Reload immediately with shorter delay
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'messages' }, () => {
        console.debug('[useChatConversations] Update en mensaje detectado');
        queueReload(100);
      })
      .subscribe();

    // Realtime for group chats - AGGRESSIVE
    const groupChatsChannel = supabase
      .channel(`group_chats_messages_${Date.now()}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'group_chats' }, () => {
        console.debug('[useChatConversations] Update en group_chats detectado, recargando');
        queueReload(50);
      })
      .subscribe();

    // Realtime for anonymous support chats
    const anonChannel = supabase
      .channel(`anonymous_support_chats_changes_${Date.now()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'anonymous_support_chats' }, (payload: any) => {
        const row = payload?.new;
        console.debug('[useChatConversations][anon][payload]', {
          eventType: payload?.eventType,
          sessionId: row?.anonymous_session_id,
          sender_type: row?.sender_type,
          created_at: row?.created_at,
          id: row?.id,
        });

        if (payload?.eventType === 'INSERT' && row?.anonymous_session_id) {
          setAnonymousSessions((prev) => {
            const sessionId = row.anonymous_session_id as string;
            const currentIndex = prev.findIndex((session: any) => session?.anonymous_session_id === sessionId);
            const isIncomingFromAnonymous = row?.sender_type === 'anonymous_user';
            const isSupportReply = row?.sender_type === 'support_admin';

            if (currentIndex === -1) {
              const initialUnread = isIncomingFromAnonymous ? 1 : 0;
              const created = {
                anonymous_session_id: sessionId,
                support_store_id: row?.support_store_id || null,
                last_message: row?.message || '',
                last_message_at: row?.created_at || new Date().toISOString(),
                message_count: 1,
                has_unread: initialUnread > 0,
                unread_count: initialUnread,
              };
              anonymousUnreadRef.current[sessionId] = initialUnread;
              console.debug('[useChatConversations][anon][session-created]', {
                sessionId,
                initialUnread,
                sender_type: row?.sender_type,
              });
              return [created, ...prev];
            }

            const current = prev[currentIndex];
            const currentUnread = typeof current?.unread_count === 'number'
              ? current.unread_count
              : (current?.has_unread ? 1 : 0);
            const nextUnread = isIncomingFromAnonymous
              ? currentUnread + 1
              : (isSupportReply ? 0 : currentUnread);

            console.debug('[useChatConversations][anon][session-update]', {
              sessionId,
              currentUnread,
              nextUnread,
              sender_type: row?.sender_type,
              currentIndex,
            });

            const updated = {
              ...current,
              last_message: row?.message || current?.last_message,
              last_message_at: row?.created_at || current?.last_message_at,
              message_count: Number(current?.message_count || 0) + 1,
              has_unread: nextUnread > 0,
              unread_count: nextUnread,
            };

            const next = [...prev];
            next[currentIndex] = updated;
            next.sort((a: any, b: any) => {
              const aTs = a?.last_message_at ? new Date(a.last_message_at).getTime() : 0;
              const bTs = b?.last_message_at ? new Date(b.last_message_at).getTime() : 0;
              return bTs - aTs;
            });

            anonymousUnreadRef.current[sessionId] = nextUnread;
            return next;
          });
        }

        queueReload(160);
      })
      .subscribe();

    const onChatSync = () => queueReload();
    const onGroupOpened = (event: Event) => {
      const customEvent = event as CustomEvent;
      const openedGroupId = customEvent?.detail?.groupId as string | undefined;
      const openedAnonymousSessionId = customEvent?.detail?.anonymousSessionId as string | undefined;
      if (!openedGroupId) return;

      setGroupChats((prev) => prev.map((group) => (
        group.id === openedGroupId
          ? { ...group, unread_count: 0 }
          : group
      )));
      groupUnreadRef.current[openedGroupId] = 0;

      if (openedAnonymousSessionId) {
        setAnonymousSessions((prev) => prev.map((session: any) => (
          session?.anonymous_session_id === openedAnonymousSessionId
            ? { ...session, has_unread: false, unread_count: 0 }
            : session
        )));
        anonymousUnreadRef.current[openedAnonymousSessionId] = 0;
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener(CHAT_SYNC_EVENT, onChatSync as EventListener);
      window.addEventListener('chat:group-opened', onGroupOpened as EventListener);
    }

    // Fallback if realtime is delayed or unavailable - aggressive polling
    const fallbackInterval = setInterval(() => {
      console.debug('[useChatConversations] Fallback polling...');
      loadConversations({ silent: true });
    }, 500);

    return () => {
      if (queueReloadRef.timer) {
        clearTimeout(queueReloadRef.timer);
      }
      clearInterval(fallbackInterval);
      if (typeof window !== 'undefined') {
        window.removeEventListener(CHAT_SYNC_EVENT, onChatSync as EventListener);
        window.removeEventListener('chat:group-opened', onGroupOpened as EventListener);
      }
      dmChannel.unsubscribe();
      groupChannel.unsubscribe();
      messagesChannel.unsubscribe();
      groupChatsChannel.unsubscribe();
      anonChannel.unsubscribe();
    };
  }, [loadConversations, session?.user?.id]);

  return { 
    conversations: dmConversations, 
    groups: groupChats, 
    anonymousSessions,
    loading, 
    error, 
    refetch: loadConversations 
  };
};

// ============================================
// useChatMessages Hook
// ============================================
interface UseChatMessagesOptions {
  conversationId?: string;
  groupId?: string;
  limit?: number;
}

interface MessageReadUser {
  userId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  readAt: string;
}

export const useChatMessages = ({ conversationId, groupId, anonymousSessionId, limit = 30 }: UseChatMessagesOptions & { anonymousSessionId?: string }) => {
  const [messages, setMessages] = useState<MessageWithSender[]>([]);
  const [readByMessageId, setReadByMessageId] = useState<Record<string, MessageReadUser[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const anonChannelRef = useRef<RealtimeChannel | null>(null);

  const areMessagesEquivalent = useCallback((a: MessageWithSender[], b: MessageWithSender[]) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      const left = a[i];
      const right = b[i];
      if (
        left.id !== right.id ||
        left.content !== right.content ||
        left.updated_at !== right.updated_at ||
        left.is_deleted !== right.is_deleted
      ) {
        return false;
      }
    }
    return true;
  }, []);

  const loadMessages = async (offset = 0, options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent);
    try {
      if (offset === 0) {
        if (!silent) setLoading(true);
      } else {
        setLoadingMore(true);
      }
      let data: MessageWithSender[];

      // Priority: anonymous user with group chat (new infrastructure)
      if (groupId && anonymousSessionId) {
        console.log('[useChatMessages] Loading anon group chat messages:', { groupId, anonymousSessionId, offset, limit });
        data = await getAnonymousGroupMessages(anonymousSessionId, limit, offset);
        console.log('[useChatMessages] Got anon group messages:', data.length, 'messages');
        // Reverse to get ascending order (oldest first) like regular chats
        data = data.reverse();
      } else if (groupId) {
        // Regular group chat (authenticated)
        console.log('[useChatMessages] Loading regular group chat:', { groupId });
        data = await getGroupMessages(groupId, limit, offset);
      } else if (anonymousSessionId) {
        // Legacy anonymous chat (non-group)
        console.log('[useChatMessages] Loading legacy anon chat:', { anonymousSessionId });
        const anonData = await getAnonymousMessages(anonymousSessionId);
        
        // Obtener el usuario actual para identificar quién es "yo"
        const { data: { user } } = await supabase.auth.getUser();

        // Transformar al formato MessageWithSender para que ChatWindow lo entienda
        data = anonData.map((m: any) => {
          // Lógica de identificación de remitente para alineación
          let senderId = m.anonymous_session_id; // Por defecto el anónimo
          if (m.sender_type === 'support_admin') {
            senderId = m.admin_id || 'support';
          }

          return {
            ...m,
            id: m.id,
            content: m.message,
            sender_id: senderId,
            sender_username: m.sender_type === 'support_admin' ? 'Support' : 'Anonymous User',
            sender_display_name: m.sender_type === 'support_admin' ? 'Support' : 'Anonymous User',
            sender_avatar_url: m.sender_type === 'support_admin' ? '/minibid-avatar.png' : null,
            created_at: m.created_at,
            type: 'text',
            conversation_id: null,
            group_chat_id: null,
            is_read: m.read,
            is_edited: false
          } as MessageWithSender;
        });
      } else if (conversationId) {
        console.log('[useChatMessages] Loading DM:', { conversationId });
        data = await getDMMessages(conversationId, limit, offset);
      } else {
        throw new Error('Either conversationId, groupId or anonymousSessionId is required');
      }

      if (offset === 0) {
        let changed = false;
        setMessages((prev) => {
          if (areMessagesEquivalent(prev, data)) return prev;
          changed = true;
          return data;
        });
        if (changed) {
          emitChatSync();
        }
      } else {
        setMessages((prev) => [...data, ...prev]);
      }

      setHasMore((anonymousSessionId && !groupId) ? false : data.length === limit);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      if (offset === 0) {
        if (!silent) setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const loadReadReceipts = useCallback(async (targetMessages?: MessageWithSender[]) => {
    if (!groupId && !conversationId) {
      setReadByMessageId({});
      return;
    }

    const source = targetMessages || messages;
    const messageIds = source
      .map((m) => m.id)
      .filter((id) => Boolean(id) && !String(id).startsWith('temp-'));

    if (!messageIds.length) {
      setReadByMessageId({});
      return;
    }

    const { data, error } = await supabase
      .from('message_read_status')
      .select('message_id, user_id, read_at')
      .in('message_id', messageIds);

    if (error) {
      console.error('[useChatMessages] Error loading read receipts:', error);
      return;
    }

    const readerIds = Array.from(
      new Set(((data || []) as any[]).map((row) => row.user_id).filter(Boolean))
    );

    const { data: profileRows, error: profileError } = readerIds.length
      ? await supabase
          .from('profiles')
          .select('id, username, display_name, avatar_url')
          .in('id', readerIds)
      : { data: [] as any[], error: null as any };

    if (profileError) {
      console.error('[useChatMessages] Error loading reader profiles:', profileError);
    }

    const profileById = new Map(
      (profileRows || []).map((profile: any) => [profile.id, profile])
    );

    const grouped: Record<string, MessageReadUser[]> = {};
    for (const row of (data || []) as any[]) {
      const profile = profileById.get(row.user_id) || {};
      const nextReader: MessageReadUser = {
        userId: row.user_id,
        username: profile.username || 'Usuario',
        displayName: profile.display_name ?? null,
        avatarUrl: profile.avatar_url ?? null,
        readAt: row.read_at,
      };

      if (!grouped[row.message_id]) grouped[row.message_id] = [];
      grouped[row.message_id].push(nextReader);
    }

    Object.keys(grouped).forEach((messageId) => {
      grouped[messageId].sort(
        (a, b) => new Date(b.readAt).getTime() - new Date(a.readAt).getTime()
      );
    });

    setReadByMessageId(grouped);
  }, [conversationId, groupId, messages]);

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      loadMessages(messages.length);
    }
  };

  const sendMessage = async (params: SendMessageParams) => {
    try {
      // Handle anonymous group chats first (new infrastructure)
      if (groupId && anonymousSessionId) {
        await sendAnonymousUserGroupMessage(groupId, anonymousSessionId, params.content);
        
        // Re-sync to get the new message from server
        setTimeout(() => {
          loadMessages(0, { silent: true });
        }, 120);
        return;
      }

      // Handle legacy anonymous chats (non-group)
      if (anonymousSessionId) {
        await sendAnonymousChatReply(anonymousSessionId, params.content);
        
        // Optimización: añadir el mensaje localmente de inmediato para feedback instantáneo
        const { data: { user } } = await supabase.auth.getUser();
        const tempMsg: MessageWithSender = {
          id: `temp-${Date.now()}`,
          content: params.content,
          sender_id: user?.id || 'support',
          sender_username: 'Support',
          sender_display_name: 'Support',
          sender_avatar_url: null,
          created_at: new Date().toISOString(),
          type: 'text',
          conversation_id: null,
          group_chat_id: null,
          is_read: true,
          is_edited: false
        };
        setMessages(prev => [...prev, tempMsg]);
        emitChatSync();

        // Re-sync quickly in case realtime is delayed.
        setTimeout(() => {
          loadMessages(0, { silent: true });
        }, 120);
        return;
      }

      if (conversationId) {
        // En DMs, el recipient_id es obligatorio para el RPC send_dm_message.
        // Fallback: si no viene desde UI, resolverlo desde dm_conversations.
        let rid = params.recipient_id;

        if (!rid) {
          const { data: { user } } = await supabase.auth.getUser();
          if (user?.id) {
            const { data: conv, error: convError } = await supabase
              .from('dm_conversations')
              .select('user1_id, user2_id')
              .eq('id', conversationId)
              .maybeSingle();

            if (convError) {
              console.error('[useChatMessages] Error resolving DM recipient from conversation', {
                conversationId,
                convError,
              });
            }

            if (conv) {
              rid = conv.user1_id === user.id ? conv.user2_id : conv.user1_id;
            }
          }
        }

        if (!rid) {
          console.error('[useChatMessages] No recipient_id available for DM', { params, conversationId });
          throw new Error('No se pudo determinar el destinatario del mensaje (recipient_id).');
        }

        await sendDMMessage({ 
          ...params, 
          recipient_id: rid 
        });
      } else if (groupId) {
        await sendGroupMessage({ ...params, group_id: groupId });
      }
      emitChatSync();
      // Message will be added via realtime subscription
    } catch (err: any) {
      console.error('[useChatMessages] Error sending message:', err);
      throw err;
    }
  };

  const markAsRead = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const unreadMessageIds = messages
        .filter((m) => m.sender_id !== user?.id)
        .map((m) => m.id);

      if (unreadMessageIds.length > 0) {
        await markMessagesAsRead(unreadMessageIds);
        emitChatSync();
      }
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  };

  useEffect(() => {
    if (!conversationId && !groupId && !anonymousSessionId) return;

    loadMessages();

    // Polling fallback only for anonymous sessions.
    let interval: NodeJS.Timeout | null = null;
    if (anonymousSessionId) {
      // Increased from 1400ms to 5000ms to reduce egress
      interval = setInterval(() => loadMessages(0, { silent: true }), 5000);

      anonChannelRef.current = supabase
        .channel(`anonymous_support_messages_${anonymousSessionId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'anonymous_support_chats',
            filter: `anonymous_session_id=eq.${anonymousSessionId}`,
          },
          () => {
              loadMessages(0, { silent: true });
            emitChatSync();
          }
        )
        .subscribe();
    }

    // Subscribe to realtime message updates (solo si no es anónimo)
    if (!anonymousSessionId) {
      const chatId = conversationId || groupId;
      const table = conversationId ? 'dm_conversation_id' : 'group_chat_id';

      // Fallback fetch for UI reliability when realtime drops events.
      // Increased from 2500ms to 10000ms to reduce egress
      interval = setInterval(() => {
        loadMessages(0, { silent: true });
      }, 10000);

      channelRef.current = supabase
        .channel(`messages_${chatId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `${table}=eq.${chatId}`,
          },
          (payload) => {
            // Fetch the new message with sender info
            const newMessage = payload.new as any;
            supabase
              .from('profiles')
              .select('username, avatar_url, display_name')
              .eq('id', newMessage.sender_id)
              .single()
              .then(({ data: profile }) => {
                setMessages((prev) => {
                  if (prev.some((msg) => msg.id === newMessage.id)) return prev;
                  return [
                    ...prev,
                    {
                      ...newMessage,
                      sender_username: profile?.username || 'Unknown',
                      sender_display_name: profile?.display_name ?? null,
                      sender_avatar_url: profile?.avatar_url,
                    },
                  ];
                });
                emitChatSync();
              });
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'messages',
            filter: `${table}=eq.${chatId}`,
          },
          (payload) => {
            setMessages((prev) =>
              prev.map((msg) => (msg.id === payload.new.id ? { ...msg, ...(payload.new as any) } : msg))
            );
            emitChatSync();
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'messages',
            filter: `${table}=eq.${chatId}`,
          },
          (payload) => {
            setMessages((prev) => prev.filter((msg) => msg.id !== payload.old.id));
            emitChatSync();
          }
        )
        .subscribe();
    }

    return () => {
      if (interval) clearInterval(interval);
      channelRef.current?.unsubscribe();
      anonChannelRef.current?.unsubscribe();
    };
  }, [conversationId, groupId, anonymousSessionId]);

  useEffect(() => {
    if ((!groupId && !conversationId) || !messages.length) {
      if (!groupId && !conversationId) setReadByMessageId({});
      return;
    }

    void loadReadReceipts(messages);
  }, [conversationId, groupId, messages, loadReadReceipts]);

  return {
    messages,
    readByMessageId,
    loading,
    error,
    hasMore,
    loadingMore,
    loadMore,
    sendMessage,
    markAsRead,
    refetch: () => loadMessages(0),
  };
};

// ============================================
// useTypingIndicator Hook
// ============================================
interface UseTypingIndicatorOptions {
  conversationId?: string;
  groupId?: string;
}

export const useTypingIndicator = ({ conversationId, groupId }: UseTypingIndicatorOptions) => {
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const setTyping = useCallback(
    async (isTyping: boolean) => {
      try {
        if (isTyping) {
          await updateUserPresence('online', conversationId, groupId);

          // Clear existing timeout
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }

          // Auto-stop typing after 3 seconds
          typingTimeoutRef.current = setTimeout(() => {
            updateUserPresence('online', undefined, undefined);
          }, 3000);
        } else {
          await updateUserPresence('online', undefined, undefined);
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }
        }
      } catch (err) {
        console.error('Error updating typing status:', err);
      }
    },
    [conversationId, groupId]
  );

  useEffect(() => {
    if (!conversationId && !groupId) return;

    const fetchTypingUsers = async () => {
      try {
        const users = conversationId
          ? await getTypingUsersInDM(conversationId)
          : groupId
          ? await getTypingUsersInGroup(groupId)
          : [];
        setTypingUsers(users);
      } catch (err) {
        console.error('Error fetching typing users:', err);
      }
    };

    fetchTypingUsers();

    // Poll for typing users every 2 seconds
    // Increased from 2000ms to 10000ms to reduce egress
    const interval = setInterval(fetchTypingUsers, 10000);

    // Subscribe to presence updates
    const channel = supabase
      .channel('typing_updates')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'user_presence' }, () => {
        fetchTypingUsers();
      })
      .subscribe();

    return () => {
      clearInterval(interval);
      channel.unsubscribe();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [conversationId, groupId]);

  return {
    typingUsers,
    setTyping,
  };
};

// ============================================
// useUserPresence Hook
// ============================================
export const useUserPresence = (userIds: string[]) => {
  const [presences, setPresences] = useState<Record<string, UserPresence>>({});
  const [loading, setLoading] = useState(true);
  const userIdsKey = userIds.join(',');

  const refetchPresences = useCallback(async () => {
    if (!userIds.length) {
      setPresences({});
      setLoading(false);
      return;
    }

    try {
      const data = await getUsersPresence(userIds);
      const presenceMap = data.reduce((acc, p) => {
        acc[p.user_id] = p;
        return acc;
      }, {} as Record<string, UserPresence>);
      setPresences(presenceMap);
    } catch (err) {
      console.error('Error fetching user presences:', err);
    } finally {
      setLoading(false);
    }
  }, [userIdsKey]);

  useEffect(() => {
    setLoading(Boolean(userIds.length));
    void refetchPresences();
  }, [userIdsKey, refetchPresences]);

  useEffect(() => {
    if (!userIds.length) return;

    const trackedIds = new Set(userIds);
    const channel = supabase
      .channel(`presence_updates_${userIdsKey}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_presence' }, (payload) => {
        const presence = payload.new as UserPresence | null;
        if (!presence?.user_id || !trackedIds.has(presence.user_id)) return;
        setPresences((prev) => ({
          ...prev,
          [presence.user_id]: presence,
        }));
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [userIdsKey]);

  return {
    presences,
    loading,
    refetch: refetchPresences,
    isOnline: (userId: string) => isUserPresenceActive(presences[userId]),
    getStatus: (userId: string) => presences[userId]?.status || 'offline',
  };
};

// ============================================
// useOnlineStatus Hook (for current user)
// ============================================
export const useOnlineStatus = (hasActiveChatWindow = false) => {
  const [status, setStatus] = useState<'online' | 'offline' | 'away'>('online');
  const hasActiveChatRef = useRef(hasActiveChatWindow);
  hasActiveChatRef.current = hasActiveChatWindow;

  useEffect(() => {
    updateUserPresence('online');

    const handleFocus = () => {
      updateUserPresence('online');
      setStatus('online');
    };

    const handleBlur = () => {
      if (hasActiveChatRef.current) {
        updateUserPresence('online');
        setStatus('online');
        return;
      }
      updateUserPresence('away');
      setStatus('away');
    };

    const handlePageHide = () => {
      updateUserPresence('offline');
      setStatus('offline');
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('pagehide', handlePageHide);

    const heartbeat = setInterval(() => {
      if (document.hasFocus() || hasActiveChatRef.current) {
        updateUserPresence('online');
      }
    }, 30000);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pagehide', handlePageHide);
      clearInterval(heartbeat);
    };
  }, []);

  useEffect(() => {
    if (hasActiveChatWindow) {
      updateUserPresence('online');
      setStatus('online');
    }
  }, [hasActiveChatWindow]);

  return status;
};

// ============================================
// useChatUnreadCount Hook
// ============================================
export const useChatUnreadCount = () => {
  const [updateSignal, setUpdateSignal] = useState(0);
  const { conversations, groups, anonymousSessions } = useChatConversations();
  const { session } = useSession();
  const [anonymousLocalUnread, setAnonymousLocalUnread] = useState(0);

  const isAnonymousUser = !session?.user?.id;

  useEffect(() => {
    if (!isAnonymousUser) {
      setAnonymousLocalUnread(0);
      return;
    }

    const recomputeLocalUnread = () => {
      const totalLocalUnread = listAnonymousSupportConversations().reduce((sum, conv) => {
        return sum + (conv.unreadCount || 0);
      }, 0);
      setAnonymousLocalUnread(totalLocalUnread);
    };

    recomputeLocalUnread();

    const handleStorage = () => recomputeLocalUnread();
    window.addEventListener('storage', handleStorage);
    window.addEventListener('chat:sync', handleStorage);
    window.addEventListener('anonymous-support-conversations-updated', handleStorage);

    // Increased from 1500ms to 10000ms to reduce egress
    const intervalId = setInterval(recomputeLocalUnread, 10000);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('chat:sync', handleStorage);
      window.removeEventListener('anonymous-support-conversations-updated', handleStorage);
      clearInterval(intervalId);
    };
  }, [isAnonymousUser]);

  // Listen for message inserts to force immediate update
  useEffect(() => {
    const messageChannel = supabase
      .channel(`unread_count_monitor_${Date.now()}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload) => {
          console.debug('[useChatUnreadCount] Detectado nuevo mensaje', payload);
          setUpdateSignal(prev => prev + 1);
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'anonymous_support_chats' },
        (payload) => {
          console.debug('[useChatUnreadCount] Detectado nuevo mensaje anónimo', payload);
          setUpdateSignal(prev => prev + 1);
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'group_chats' },
        (payload) => {
          console.debug('[useChatUnreadCount] Detectado cambio en group_chats', payload);
          setUpdateSignal(prev => prev + 1);
        }
      )
      .subscribe();

    return () => {
      messageChannel.unsubscribe();
    };
  }, []);

  // Messenger-like badge: total unread messages/items, not just number of threads.
  const unreadDMs = conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
  const unreadGroups = groups.reduce((sum, group) => sum + (group.unread_count || 0), 0);
  const unreadAnonymous = (anonymousSessions || []).reduce((sum, session: any) => {
    if (typeof session?.unread_count === 'number') return sum + session.unread_count;
    return sum + (session?.has_unread ? 1 : 0);
  }, 0);

  useEffect(() => {
    const sessionsDebug = (anonymousSessions || []).map((session: any) => ({
      sessionId: session?.anonymous_session_id,
      unread_count: session?.unread_count,
      has_unread: session?.has_unread,
      last_message_at: session?.last_message_at,
    }));

    console.debug('[useChatUnreadCount][anon][sessions]', sessionsDebug);
  }, [anonymousSessions, updateSignal]);

  const total = unreadDMs + unreadGroups + unreadAnonymous + (isAnonymousUser ? anonymousLocalUnread : 0);
  console.debug('[useChatUnreadCount] Calculado:', {
    unreadDMs,
    unreadGroups,
    unreadAnonymous,
    anonymousLocalUnread,
    total,
    numGroups: groups.length,
  });
  
  // Return depends on updateSignal to ensure fresh calculation on each message
  return total;
};

interface ChatUnreadSource {
  conversations: DMConversationWithDetails[];
  groups: GroupChatWithDetails[];
  anonymousSessions: any[];
}

export const useChatUnreadCountFromSource = ({
  conversations,
  groups,
  anonymousSessions,
}: ChatUnreadSource) => {
  const [updateSignal, setUpdateSignal] = useState(0);
  const { session } = useSession();
  const [anonymousLocalUnread, setAnonymousLocalUnread] = useState(0);

  const isAnonymousUser = !session?.user?.id;

  useEffect(() => {
    if (!isAnonymousUser) {
      setAnonymousLocalUnread(0);
      return;
    }

    const recomputeLocalUnread = () => {
      const totalLocalUnread = listAnonymousSupportConversations().reduce((sum, conv) => {
        return sum + (conv.unreadCount || 0);
      }, 0);
      setAnonymousLocalUnread(totalLocalUnread);
    };

    recomputeLocalUnread();

    const handleStorage = () => recomputeLocalUnread();
    window.addEventListener('storage', handleStorage);
    window.addEventListener('chat:sync', handleStorage);
    window.addEventListener('anonymous-support-conversations-updated', handleStorage);

    // Increased from 1500ms to 10000ms to reduce egress
    const intervalId = setInterval(recomputeLocalUnread, 10000);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('chat:sync', handleStorage);
      window.removeEventListener('anonymous-support-conversations-updated', handleStorage);
      clearInterval(intervalId);
    };
  }, [isAnonymousUser]);

  // Listen for message inserts to force immediate update
  useEffect(() => {
    const messageChannel = supabase
      .channel(`unread_count_monitor_${Date.now()}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        () => {
          setUpdateSignal(prev => prev + 1);
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'anonymous_support_chats' },
        () => {
          setUpdateSignal(prev => prev + 1);
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'group_chats' },
        () => {
          setUpdateSignal(prev => prev + 1);
        }
      )
      .subscribe();

    return () => {
      messageChannel.unsubscribe();
    };
  }, []);

  const unreadDMs = conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
  const unreadGroups = groups.reduce((sum, group) => sum + (group.unread_count || 0), 0);
  const unreadAnonymous = (anonymousSessions || []).reduce((sum, session: any) => {
    if (typeof session?.unread_count === 'number') return sum + session.unread_count;
    return sum + (session?.has_unread ? 1 : 0);
  }, 0);

  const total = unreadDMs + unreadGroups + unreadAnonymous + (isAnonymousUser ? anonymousLocalUnread : 0);

  // Keep dependency on updateSignal so realtime updates trigger recalculation.
  void updateSignal;

  return total;
};

// ============================================
// useAnonymousChatMessages Hook
// For non-authenticated users chatting with support
// ============================================
interface UseAnonymousChatMessagesOptions {
  anonymousSessionId?: string;
  supportStoreId?: string;
  supportStoreName?: string;
  supportStoreAvatarUrl?: string | null;
}

export const useAnonymousChatMessages = ({
  anonymousSessionId,
  supportStoreId,
  supportStoreName,
  supportStoreAvatarUrl,
}: UseAnonymousChatMessagesOptions) => {
  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const areAnonymousMessagesEquivalent = useCallback((a: any[], b: any[]) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (
        a[i]?.id !== b[i]?.id ||
        a[i]?.message !== b[i]?.message ||
        a[i]?.read !== b[i]?.read ||
        a[i]?.updated_at !== b[i]?.updated_at
      ) {
        return false;
      }
    }
    return true;
  }, []);


  const effectiveSupportStoreId = supportStoreId || getDefaultSupportStoreId();

  const updateAnonSidebar = (msgs: any[]) => {
    if (!anonymousSessionId) return;
    if (!msgs || msgs.length === 0) {
      clearAnonymousSupportConversationOnly(effectiveSupportStoreId);
      return;
    }
    const lastMsg = msgs[msgs.length - 1];
    
    // Si somos usuario anónimo (no staff), contamos mensajes sin leer del staff
    // Si somos staff, NO actualizamos el localStorage del usuario anónimo
    const isStaff = !!localStorage.getItem('is_staff');
    if (!isStaff) {
      const unreadCount = msgs.filter(m => m.sender_type === 'support_admin' && !m.read).length;
      setAnonymousSupportConversation(effectiveSupportStoreId, {
        sessionId: anonymousSessionId,
        storeName: supportStoreName,
        storeAvatarUrl: supportStoreAvatarUrl,
        lastMessage: lastMsg.message,
        lastMessageAt: lastMsg.created_at,
        unreadCount,
      });
    }
  };

  const loadMessages = async (isInitial = false) => {
    if (!anonymousSessionId) {
      setMessages([]);
      setLoading(false);
      updateAnonSidebar([]);
      return;
    }

    try {
      if (isInitial) setLoading(true);
      const data = await getAnonymousMessages(anonymousSessionId);
      
      // Manejar sonido si hay mensajes nuevos
      if (data.length > 0 && messages.length > 0) {
        const lastOldId = messages[messages.length - 1].id;
        const lastNewId = data[data.length - 1].id;
        if (lastOldId !== lastNewId) {
          // Play sound ONLY for incoming messages (different sender than us)
          // As we don't have current user id here easily without more logic, 
          // we check the sender_type of the last message.
          const lastMsg = data[data.length - 1];
          // If anonymous session is used by User, play if sender is Admin
          // If anonymous session is used by Admin, play if sender is User
          // LocalStorage check for role context
          const isStaff = !!localStorage.getItem('is_staff'); // O similar
          if ((isStaff && lastMsg.sender_type === 'anonymous_user') || 
              (!isStaff && lastMsg.sender_type === 'support_admin')) {
            playNotificationSound();
          }
        }
      }

      let changed = false;
      setMessages((prev) => {
        if (areAnonymousMessagesEquivalent(prev, data)) return prev;
        changed = true;
        return data;
      });
      setError(null);
      updateAnonSidebar(data);
      if (changed) {
        emitChatSync();
      }
    } catch (err) {
      setError(err as Error);
      setMessages([]);
      updateAnonSidebar([]);
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  // Polling para recibir respuestas en tiempo real (sin doble llamado)
  useEffect(() => {
    if (!anonymousSessionId) return;
    let mounted = true;
    
    // Carga inicial y obtención del último ID para el sonido
    const init = async () => {
      await loadMessages(true);
    };
    init();

    const channel = supabase
      .channel(`anonymous_chat_messages_${anonymousSessionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'anonymous_support_chats',
          filter: `anonymous_session_id=eq.${anonymousSessionId}`,
        },
        () => {
          loadMessages(false);
        }
      )
      .subscribe();

    const interval = setInterval(() => {
      if (mounted) {
        loadMessages(false).then(() => {
          // Si hay mensajes nuevos de soporte o del usuario y no somos nosotros, sonar
          // Nota: Como staff usamos este hook, queremos sonar cuando llega algo del anónimo.
          // Como usuario anónimo, queremos sonar cuando llega algo del soporte.
        });
      }
    }, 2000);
    return () => {
      mounted = false;
      channel.unsubscribe();
      clearInterval(interval);
    };
  }, [anonymousSessionId]);

  const sendMessage = async (content: string) => {
    if (!anonymousSessionId) {
      throw new Error('Anonymous session ID is required');
    }

    try {
      const messageId = await saveAnonymousMessage(anonymousSessionId, content, 'anonymous_user', effectiveSupportStoreId);
      const newMsg = {
        id: messageId,
        anonymous_session_id: anonymousSessionId,
        message: content,
        sender_type: 'anonymous_user',
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => {
        const updated = [...prev, newMsg];
        updateAnonSidebar(updated);
        return updated;
      });
      emitChatSync();
      return messageId;
    } catch (err) {
      console.error('Error sending anonymous message:', err);
      throw err;
    }
  };

  // Eliminar el useEffect redundante que causa flicker

  return { messages, loading, error, sendMessage };
};
